import type { RuleRef } from "./rules.js";

/** All offsets are half-open JavaScript UTF-16 code-unit positions in the original input. */
export type FindingCategory = "personal" | "dictionary" | "secret";
export type FindingSeverity = "info" | "warning" | "critical";
export type FindingStatus = "candidate" | "validated" | "manual";
export type FindingSource = "rule" | "dictionary" | "model" | "manual";
export type Country = "core" | "se" | "dk" | "lt" | "no" | "fi";

export interface Finding {
  id: string;
  startUtf16: number;
  endUtf16: number;
  rawType: string;
  displayType: string;
  category: FindingCategory;
  severity: FindingSeverity;
  confidence: number;
  status: FindingStatus;
  source: FindingSource;
  /** Required, exact versioned detector identity. Never infer this from type. */
  rule: RuleRef;
  country?: Country;
  validation?: string[];
  /** Present for dictionary findings only; never intended for telemetry. */
  dictionaryEntryId?: string;
}

export interface DictionaryEntry {
  id: string;
  canonicalValue: string;
  aliases?: readonly string[];
  type: string;
  caseSensitive?: boolean;
  /** Defaults to true to prevent accidental substring replacement. */
  wholeWordOnly?: boolean;
  placeholderPrefix?: string;
}

export interface DetectionOptions {
  dictionary?: readonly DictionaryEntry[];
  countries?: readonly Country[];
  /** Exact id+version allow-list, applied before overlap resolution. */
  enabledRules?: readonly RuleRef[];
  /** Frozen YYYY-MM-DD date used by time-sensitive country validators. */
  referenceDate?: string;
  signal?: AbortSignal;
  /** Defaults to 250,000 UTF-16 code units. */
  maxInputLength?: number;
}

export interface DetectionResult {
  findings: Finding[];
  /** Candidates suppressed by deterministic overlap resolution. Keep only in caller memory. */
  suppressedFindings: Finding[];
}

export class InputLimitError extends Error {
  readonly code = "INPUT_LIMIT";
  constructor() {
    super("Input exceeds the configured scan limit.");
    this.name = "InputLimitError";
  }
}

export class ScanCancelledError extends Error {
  readonly code = "SCAN_CANCELLED";
  constructor() {
    super("Scan was cancelled.");
    this.name = "ScanCancelledError";
  }
}

export class DictionaryInputError extends Error {
  readonly code = "INVALID_DICTIONARY";
  constructor() {
    super("Dictionary input is invalid or exceeds its limits.");
    this.name = "DictionaryInputError";
  }
}
